import { ReactNode } from "react";
import { useAuth, AppRole } from "@/contexts/AuthContext";
import { useAmIDsl } from "@/hooks/useDsl";

interface RequireRoleProps {
  children: ReactNode;
  allowed: AppRole[];
  /** Let a designated safeguarding lead in whatever their access level is. */
  allowDsl?: boolean;
}

/**
 * Renders children only if the current user has one of the allowed roles.
 * Otherwise shows a friendly "no permission" page.
 */
export function RequireRole({ children, allowed, allowDsl = false }: RequireRoleProps) {
  const { role } = useAuth();
  const { isDsl, isLoading: dslLoading } = useAmIDsl();

  if (role && allowed.includes(role)) {
    return <>{children}</>;
  }

  if (allowDsl) {
    if (dslLoading) return null;
    if (isDsl) return <>{children}</>;
  }

  return <NoPermissionPage />;
}

function NoPermissionPage() {
  return (
    <div className="flex items-center justify-center min-h-[60vh] p-6">
      <div className="bg-card rounded-2xl border border-[rgba(13,31,20,0.09)] p-8 max-w-md w-full text-center space-y-4">
        <div className="text-5xl">🔒</div>
        <h2 className="text-xl font-heading font-semibold text-nursly-ink">
          You don't have permission to view this page
        </h2>
        <p className="text-sm text-nursly-muted font-body leading-relaxed">
          If you think you should have access, please speak to your manager.
        </p>
        <a
          href="/dashboard"
          className="inline-flex items-center justify-center rounded-lg bg-nursly-amber text-white px-5 py-2.5 text-sm font-medium font-body hover:bg-nursly-amber-bright transition-colors"
        >
          Go Back to Dashboard
        </a>
      </div>
    </div>
  );
}
